<template>
  <UserCommentModal :defer :pictures="review.pictures" :review>
    <template #trigger="{ open: openModal }">
      <RevCard
        ref="reviewCard"
        :class="cardClass"
        data-test="review-card"
        @click="(openModal(), onCardClick())"
      >
        <div class="group relative" :class="reviewPictureClass">
          <RevIllustration
            alt=""
            class="rounded-t-lg h-full w-full object-cover group-only-of-type:rounded-b-lg"
            data-test="review-card-picture"
            :height="400"
            :src="review.pictures[0]"
            v-bind="imageAttributes"
            :width="256"
          />

          <div
            class="absolute inset-0 group-only-of-type:rounded-b-lg"
            :class="overlayClass"
          />

          <slot name="card-header">
            <RevTag
              v-if="review.customer.firstName && review.customer.lastName"
              class="absolute left-16 top-16"
              :label="`${review.customer.firstName} ${review.customer.lastName}`"
              variant="secondary"
            />
          </slot>

          <slot name="card-body" :openModal>
            <div
              class="mood-inverse absolute bottom-16 left-16 right-16 flex flex-col gap-16"
            >
              <div class="text-static-default-hi body-2 line-clamp-4">
                {{ review.comment }}
              </div>

              <RevRating :score="review.averageRate" size="extra-small" />
            </div>
          </slot>
        </div>

        <slot name="card-footer">
          <div class="flex grow gap-16 p-16">
            <RevIllustration
              alt=""
              class="!size-52"
              :height="52"
              sizes="52px"
              :src="review.product.imageUrl"
              :width="52"
            />

            <div class="text-static-default-hi caption">
              {{ review.product.title }}
            </div>
          </div>
        </slot>
      </RevCard>
    </template>
  </UserCommentModal>
</template>

<script lang="ts" setup>
import { onBeforeUnmount, useTemplateRef } from 'vue'

import UserCommentModal from '@backmarket/nuxt-layer-reviews/review-display/UserCommentModal.vue'
import { tw } from '@backmarket/utils/string/tw'
import { RevCard } from '@ds/components/Card'
import { RevIllustration } from '@ds/components/Illustration'
import { closeModal } from '@ds/components/ModalBase'
import { RevRating } from '@ds/components/Rating'
import { RevTag } from '@ds/components/Tag'

import type { Review } from './ReviewsView.vue'

withDefaults(
  defineProps<{
    defer?: boolean
    review: Review
    overlayClass?: string
    reviewPictureClass?: string
    imageAttributes?: Record<string, string | string[] | (() => void)>
    cardClass?: string
  }>(),
  {
    defer: false,
    reviewPictureClass: tw`h-400`,
    cardClass: tw`h-full cursor-pointer hover:drop-shadow-xl`,
    overlayClass: 'defaultOverlayClass',
    imageAttributes: () => ({}),
  },
)

const emit = defineEmits<{
  (e: 'click'): void
}>()

const reviewCard = useTemplateRef('reviewCard')

defineExpose({
  reviewCard,
})

function onCardClick() {
  emit('click')
}

onBeforeUnmount(() => closeModal())
</script>

<style scoped>
.defaultOverlayClass {
  background: linear-gradient(180deg, rgba(29, 32, 36, 0) 49.5%, #1d2024 80.5%);
}
</style>
